import { useEffect, useState, useCallback, useRef } from 'react'
import { useBlocker } from 'react-router-dom'

interface UseUnsavedChangesOptions {
  isDirty: boolean
  onNavigateAway?: () => void
}

export function useUnsavedChanges({ isDirty, onNavigateAway }: UseUnsavedChangesOptions) {
  const [showDialog, setShowDialog] = useState(false)
  const [confirmedNavigation, setConfirmedNavigation] = useState(false)
  const navigationAllowedRef = useRef(false)

  // Block navigation when there are unsaved changes
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty &&
      !confirmedNavigation &&
      !navigationAllowedRef.current &&
      currentLocation.pathname !== nextLocation.pathname
  )

  // Handle browser beforeunload event (refresh, close tab)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault()
        e.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isDirty])

  // Show dialog when navigation is blocked
  useEffect(() => {
    if (blocker.state === 'blocked') {
      setShowDialog(true)
    }
  }, [blocker.state])

  const handleConfirmNavigation = useCallback(() => {
    setShowDialog(false)
    setConfirmedNavigation(true)
    onNavigateAway?.()

    // Use setTimeout to allow state to update before proceeding
    setTimeout(() => {
      if (blocker.state === 'blocked') {
        blocker.proceed()
      }
    }, 0)
  }, [blocker, onNavigateAway])

  const handleCancelNavigation = useCallback(() => {
    setShowDialog(false)
    if (blocker.state === 'blocked') {
      blocker.reset()
    }
  }, [blocker])

  // Method to allow programmatic navigation bypass (for form submissions)
  const allowNavigation = useCallback(() => {
    navigationAllowedRef.current = true
    setConfirmedNavigation(true)
    setShowDialog(false)
    if (blocker.state === 'blocked') {
      blocker.reset()
    }
  }, [blocker])

  // Reset navigation allowed ref when isDirty changes to false
  useEffect(() => {
    if (!isDirty) {
      navigationAllowedRef.current = false
      setConfirmedNavigation(false)
    }
  }, [isDirty])

  return {
    showDialog,
    handleConfirmNavigation,
    handleCancelNavigation,
    confirmedNavigation,
    setConfirmedNavigation,
    allowNavigation,
  }
}
